import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { useCollectionData } from "../hooks/useCollectionData";
import { useSectionVisibility } from "../hooks/useSectionVisibility";
import { PORTFOLIO as FALLBACK_PORTFOLIO } from "../data/content";

const getImageSrc = (item) => {
  if (item.src) return item.src;
  if (item.imageRef?.content) return item.imageRef.content;
  if (item.imageRef?.url) return item.imageRef.url;
  if (typeof item.imageRef === "string" && item.imageRef.startsWith("http")) return item.imageRef;
  if (typeof item.imageRef === "string") return item.imageRef;
  return "https://images.unsplash.com/photo-1570212773364-e30cd076539e?w=400";
};

export default function Portfolio() {
  const visibility = useSectionVisibility();
  const { data: portfolioItems } = useCollectionData(
    "portfolios",
    FALLBACK_PORTFOLIO
  );
  const [active, setActive] = useState(null);
  const ITEMS_PER_PAGE = 8;
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const visibleItems = portfolioItems.slice(0, visibleCount);
  const hasMore = visibleCount < portfolioItems.length;

  if (!visibility.portfolio) return null;

  const next = () =>
    setActive((i) => (i + 1) % portfolioItems.length);
  const prev = () =>
    setActive(
      (i) => (i - 1 + portfolioItems.length) % portfolioItems.length
    );

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative bg-[#0a0908] py-28 sm:py-36 border-t border-[#d4af37]/10"
    >
      <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-16">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div>
            <div className="eyebrow mb-6">Karya Pilihan</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#fdfbf7] font-light leading-[1.05] tracking-tight">
              Galeri
              <span className="italic text-[#d4af37]"> mahakarya</span>
              <span className="block">para pengantin kami.</span>
            </h2>
          </div>
          <p className="text-[#a6a196] max-w-md leading-relaxed">
            Setiap bingkai adalah saksi bisu cerita cinta yang
            kami rayakan — disusun penuh dedikasi oleh tim
            Adibah.
          </p>
        </div>

        {/* Uniform gallery grid — no size classes, all cards equal */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {visibleItems.map((p, i) => (
            <motion.button
              key={p.id || i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: i * 0.05, duration: 0.7 }}
              onClick={() => setActive(i)}
              data-testid={`portfolio-item-${i}`}
              className="group relative overflow-hidden bg-[#1a1816] aspect-[4/5] w-full"
            >
              {/* Image */}
              <img
                src={getImageSrc(p)}
                alt={p.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
              />

              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0908]/95 via-[#0a0908]/30 via-40% to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-500" />

              {/* Subtle border highlight on hover */}
              <div className="absolute inset-0 border border-transparent group-hover:border-[#d4af37]/30 transition-colors duration-500" />

              {/* Content — slides up on hover */}
              <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6 text-left">
                <p className="text-[9px] tracking-[0.35em] uppercase text-[#d4af37] mb-2 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  {p.category}
                </p>
                <h3 className="font-serif text-xl sm:text-2xl text-[#fdfbf7] translate-y-3 opacity-90 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-700">
                  {p.title}
                </h3>
              </div>

              {/* Gold corner accent */}
              <div className="absolute top-3 right-3 w-8 h-8 border border-[#d4af37]/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:bg-[#d4af37]">
                <ArrowUpRight className="w-3.5 h-3.5 text-[#0a0908]" />
              </div>
            </motion.button>
          ))}
        </div>

        {/* Load More */}
        {hasMore && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex justify-center mt-12"
          >
            <button
              type="button"
              onClick={() => setVisibleCount((prev) => prev + ITEMS_PER_PAGE)}
              className="group relative bg-transparent border border-[#d4af37]/50 text-[#d4af37] px-10 py-4 text-xs tracking-[0.3em] uppercase font-semibold overflow-hidden transition-all duration-500 hover:bg-[#d4af37] hover:text-[#0a0908]"
            >
              <span className="relative z-10 transition-colors duration-500">
                Lihat Lainnya
              </span>
            </button>
          </motion.div>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {active !== null && (
          <motion.div
            key="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="portfolio-lightbox"
            className="fixed inset-0 z-[100] bg-[#0a0908]/97 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
            onClick={() => setActive(null)}
          >
            <motion.button
              data-testid="lightbox-close"
              onClick={() => setActive(null)}
              className="absolute top-6 right-6 w-12 h-12 border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#0a0908] flex items-center justify-center transition-all z-10"
            >
              <X size={20} />
            </motion.button>

            <motion.button
              data-testid="lightbox-prev"
              onClick={(e) => {
                e.stopPropagation();
                prev();
              }}
              className="absolute left-4 sm:left-10 top-1/2 -translate-y-1/2 w-12 h-12 border border-[#d4af37]/30 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#0a0908] flex items-center justify-center transition-all z-10"
            >
              <ChevronLeft size={20} />
            </motion.button>

            <motion.button
              data-testid="lightbox-next"
              onClick={(e) => {
                e.stopPropagation();
                next();
              }}
              className="absolute right-4 sm:right-10 top-1/2 -translate-y-1/2 w-12 h-12 border border-[#d4af37]/30 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#0a0908] flex items-center justify-center transition-all z-10"
            >
              <ChevronRight size={20} />
            </motion.button>

            <motion.div
              key={active}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.5 }}
              className="relative max-w-5xl w-full max-h-[85vh] flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={getImageSrc(portfolioItems[active])}
                alt={portfolioItems[active].title}
                className="max-h-[75vh] w-auto object-contain"
              />
              <div className="mt-6 text-center">
                <p className="text-[10px] tracking-[0.35em] uppercase text-[#d4af37] mb-2">
                  {portfolioItems[active].category}
                </p>
                <h3 className="font-serif text-3xl text-[#fdfbf7]">
                  {portfolioItems[active].title}
                </h3>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
